import React from 'react';
import { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Datatable from '../../ExtraComponents/ReusableTable2';
import {
  getAllEmailLogs
} from '../../Service/FeeProtector/FeeProtectorService';

import debounce from 'lodash.debounce';
import Swal from "sweetalert2";
import axios from "axios";
import * as Config from "../../Utils/Config";
import { X } from "lucide-react";

const PAGE_SIZE = 10;

const EMAIL_LOG_EXPORT_COLUMNS = [
  { label: "Sent", key: "Sent" },
  { label: "Client", key: "Client" },
  { label: "Client Manager", key: "Client Manager" },
  { label: "Subject", key: "Subject" },
  { label: "Type", key: "Type" },
  { label: "Trigger", key: "Trigger" },
  { label: "Fee Gap", key: "Fee Gap" },
  { label: "Status", key: "Status" },
];

const parseFeeGap = (value) => {
  const num = parseFloat(String(value ?? "").replace(/[^0-9.-]/g, ""));
  return isNaN(num) ? 0 : num;
};

const formatSentAt = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "-";
  const day = String(d.getDate()).padStart(2, "0");
  const month = d.toLocaleString("en-GB", { month: "short" });
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  return `${day} ${month} ${year}, ${hours}:${minutes}`;
};

const FeeProtectorEmailLog = () => {

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const agent_id = JSON.parse(localStorage.getItem("agent_id"));
  const user_id = JSON.parse(localStorage.getItem("details"))?.id;

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [triggerFilter, setTriggerFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("30");

  const [selectedLog, setSelectedLog] = useState(null);

  const [emailLogs, setEmailLogs] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const sentinelRef = useRef(null);

  const isSearchTriggered = useRef(false);

  const debouncedSetSearch = useMemo(
    () => debounce((value) => {
      isSearchTriggered.current = true;
      setDebouncedSearch(value);
    }, 1000),
    []
  );

  useEffect(() => {
    return () => debouncedSetSearch.cancel();
  }, [debouncedSetSearch]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    debouncedSetSearch(value);
  };

  const handleStatusFilterChange = (e) => setStatusFilter(e.target.value);
  const handleTypeFilterChange = (e) => setTypeFilter(e.target.value);
  const handleTriggerFilterChange = (e) => setTriggerFilter(e.target.value);
  const handleDateFilterChange = (e) => setDateFilter(e.target.value);

  const fetchLogs = async (pageToFetch, searchValue, filters, isReset) => {
    if (isReset) {
      setIsLoading(true);
    } else {
      setIsLoadingMore(true);
    }

    const data = {
      agent_id,
      page: pageToFetch,
      limit: PAGE_SIZE,
      search: searchValue,
      status: filters.status,
      type: filters.type,
      trigger: filters.trigger,
      days: filters.days,
    };

    const res = await getAllEmailLogs(data);

    if (isReset) {
      setIsLoading(false);
    } else {
      setIsLoadingMore(false);
    }

    if (res?.status) {
      setEmailLogs((prev) => (isReset ? (res?.data || []) : [...prev, ...(res?.data || [])]));
      setTotalRecords(res?.pagination?.total || 0);
      setHasMore(!!res?.pagination?.hasMore);
    } else {
      if (isReset) setEmailLogs([]);
      setHasMore(false);
    }
  };

  // Reset & refetch whenever the debounced search term or a filter changes (also fires on mount).
  useEffect(() => {
    setPage(1);
    setHasMore(true);
    fetchLogs(1, debouncedSearch, { status: statusFilter, type: typeFilter, trigger: triggerFilter, days: dateFilter }, true);
  }, [debouncedSearch, statusFilter, typeFilter, triggerFilter, dateFilter]);

  // Fetch next page when the scroll sentinel triggers a page increment.
  useEffect(() => {
    if (page === 1) return;
    fetchLogs(page, debouncedSearch, { status: statusFilter, type: typeFilter, trigger: triggerFilter, days: dateFilter }, false);
  }, [page]);

  // Infinite scroll: observe the sentinel row at the bottom of the table.
  useEffect(() => {
    if (!sentinelRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore && !isLoading) {
          setPage((prev) => prev + 1);
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, isLoadingMore, isLoading]);



  const notificationsCount = emailLogs.filter((log) => log.type === "Notification").length;
  const remindersCount = emailLogs.filter((log) => log.type === "Reminder").length;

  const emailLogColumns = useMemo(() => [
    {
      name: "Sent",
      selector: (row) => row.sent_at,
      cell: (row) => formatSentAt(row.sent_at),
      sortable: true,
    },
    {
      name: "Client/lead",
      selector: (row) => row?.Client?.client_name || "",
      cell: (row) => (
        <span className="email-client-name">{row?.Client?.client_name || "-"}</span>
      ),
      sortable: true,
      sortFunction: (a, b) =>
        (a?.Client?.client_name || "").localeCompare(b?.Client?.client_name || "", undefined, { sensitivity: "base" }),
    },
    {
      name: "Type",
      selector: (row) => row.type,
      cell: (row) => (
        <span className={`email-type-badge ${String(row.type || "").toLowerCase()}`}>
          {row.type}
        </span>
      ),
      sortable: true,
      sortFunction: (a, b) =>
        String(a.type || "").localeCompare(String(b.type || ""), undefined, { sensitivity: "base" }),
    },
    {
      name: "Trigger",
      selector: (row) => row.trigger,
      cell: (row) => <span className="email-trigger-badge">{row.trigger}</span>,
      sortable: true,
      sortFunction: (a, b) =>
        String(a.trigger || "").localeCompare(String(b.trigger || ""), undefined, { sensitivity: "base" }),
    },
    {
      name: "Fee Gap",
      selector: (row) => row.fee_gap,
      cell: (row) => (
        <span className={`email-fee-gap ${row.fee_gap === "£0.00" ? "zero" : ""}`}>
          {row.fee_gap || "£0.00"}
        </span>
      ),
      sortable: true,
      sortFunction: (a, b) => parseFeeGap(a.fee_gap) - parseFeeGap(b.fee_gap),
      width: "200px",
    },
    {
      name: "Status",
      selector: (row) => row.status,
      cell: (row) => (
        <span className={`email-status-badge ${String(row.status || "").toLowerCase().replace(/\s+/g, "-")}`}>
          {row.status}
        </span>
      ),
      sortable: true,
      sortFunction: (a, b) =>
        String(a.status || "").localeCompare(String(b.status || ""), undefined, { sensitivity: "base" }),
    },
    {
      name: "Actions",
      cell: (row) => (
        <button
          className="email-view-btn"
          type="button"
          title="View"
          onClick={() => setSelectedLog(row)}
        >
          <i className="ri-eye-line"></i>
        </button>
      ),
      ignoreRowClick: true,
    },
  ], []);


  const fetchAllLogsForExport = async () => {
    const data = {
      agent_id,
      page: 1,
      limit: 100000,
      search: debouncedSearch,
      status: statusFilter,
      type: typeFilter,
      trigger: triggerFilter,
      days: dateFilter,
    };
    return await getAllEmailLogs(data);
  };

  const formatLogsForExport = (rows) =>
    rows.map((row) => ({
      "Sent": formatSentAt(row.sent_at),
      "Client": row?.Client?.client_name || "-",
      "Client Manager": row?.Client?.am_name || "-",
      "Subject": row?.subject || "-",
      "Type": row?.type || "-",
      "Trigger": row?.trigger || "-",
      "Fee Gap": row?.fee_gap || "-",
      "Status": row?.status || "-",
    }));

  const handleExport = async () => {
    setIsLoading(true);
    const response = await fetchAllLogsForExport();
    setIsLoading(false);

    const rows = response?.data ?? [];
    if (!response?.status || rows.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'No Data',
        text: 'No data to export!',
      });
      return;
    }

    downloadCSV(formatLogsForExport(rows), "Email_Logs.csv");
  };

  const downloadCSV = (data, filename) => {
    if (!data || !data.length) return;
    const headers = Object.keys(data[0]);
    const csvRows = [];
    // HEADER
    csvRows.push(headers.join(","));
    // BODY
    data.forEach((row) => {
      const values = headers.map((header) => {
        let value = row[header] ?? "";
        // double quotes escape
        value = String(value).replace(/"/g, '""');
        return `"${value}"`;
      });
      csvRows.push(values.join(","));
    });
    const csvString = csvRows.join("\n");
    // UTF-8 BOM ADD
    const csvWithBom = "\uFEFF" + csvString;
    const blob = new Blob(
      [csvWithBom],
      {
        type: "text/csv;charset=utf-8;",
      }
    );
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleExportPDF = async () => {
    setIsLoading(true);
    const response = await fetchAllLogsForExport();

    const rows = response?.data ?? [];
    if (!response?.status || rows.length === 0) {
      setIsLoading(false);
      Swal.fire({
        icon: 'warning',
        title: 'No Data',
        text: 'No data to export!',
      });
      return;
    }

    const formattedData = formatLogsForExport(rows);

    try {
      const res = await axios.post(
        Config.base_url + "export-contract-pdf",
        { data: formattedData, columns: EMAIL_LOG_EXPORT_COLUMNS, type: "emailLog" },
        { responseType: "blob" }
      );

      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = "Email_Logs.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } finally {
      setIsLoading(false);
    }
  };


  useEffect(() => {
    if (isLoading) {
      if (isSearchTriggered.current) {
        isSearchTriggered.current = false;
        return;
      }
      Swal.fire({
        title: "",
        html: '<div style="height:250px;"><div class="loader" style="margin:0px auto;"></div><p class="text-white mt-3">Please Wait...</p></div>',
        showConfirmButton: false,
        allowOutsideClick: false,
        allowEscapeKey: false,
        background: "transparent",
        backdrop: "rgba(229, 233, 236, 0.2)",

        customClass: {
          popup: "no-shadow",
        },
      });
    } else {
      Swal.close();
    }
  }, [isLoading]);

  return (
    <div className="email-logs-page">
      {/* Breadcrumb */}
      <div className="email-logs-breadcrumb">
        Fee Protector
      </div>

      {/* Header */}
      <div className="email-logs-header">
        <h1>Email Logs</h1>

        <p>
          Every breach alert emailed to client managers from Fee Protector,
          with delivery status, open tracking and resend history for audit.
        </p>
      </div>

      {/* Main Card */}
      <div className="email-logs-card">

        {/* Filters */}
        <div className="email-logs-filters">

          {/* Search */}
          <div className="email-search-box">
            <i className="ri-search-line"></i>

            <input
              type="text"
              value={searchInput}
              onChange={handleSearchChange}
              placeholder="Search here"
            />
          </div>

          {/* Status */}
          <div className="email-filter-select">
            <select value={statusFilter} onChange={handleStatusFilterChange}>
              <option value="">All Status</option>
              <option value="Sent">Sent</option>
              <option value="Failed">Failed</option>
            </select>

            <i className="ri-arrow-down-s-line"></i>
          </div>

          {/* Type */}
          <div className="email-filter-select">
            <select value={typeFilter} onChange={handleTypeFilterChange}>
              <option value="">All Types</option>
              <option value="Notification">Notification</option>
              <option value="Reminder">Reminder</option>
            </select>

            <i className="ri-arrow-down-s-line"></i>
          </div>

          {/* Breach Type */}
          <div className="email-filter-select">
            <select value={triggerFilter} onChange={handleTriggerFilterChange}>
              <option value="">All Breach Types</option>
              <option value="Overdue">Overdue for Review</option>
              <option value="Due for Review">Due for Review</option>
              <option value="Threshold Breach">Threshold Breach</option>
            </select>

            <i className="ri-arrow-down-s-line"></i>
          </div>

          {/* Date */}
          <div className="email-filter-select email-date-filter">
            <select value={dateFilter} onChange={handleDateFilterChange}>
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="365">Last Year</option>
            </select>

            <i className="ri-arrow-down-s-line"></i>
          </div>
          <div className="email-export-buttons">
            <button
              type="button"
              className="email-export-btn"
              onClick={handleExportPDF}
            >
              <i className="ri-download-2-line"></i>
              Export PDF
            </button>

            <button
              type="button"
              className="email-export-btn"
              onClick={handleExport}
            >
              <i className="ri-download-2-line"></i>
              Export CSV
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="email-logs-table-wrapper">

          <Datatable
            columns={emailLogColumns}
            data={emailLogs}
           // loading={isLoading}
          />

          {/* Scroll sentinel: loads the next page when scrolled into view */}
          <div ref={sentinelRef} className="email-scroll-sentinel">
            {isLoadingMore ? "Loading more..." : ""}
          </div>
        </div>

        {/* Footer */}
        <div className="email-logs-footer">

          <div className="email-footer-summary">
            <span>Showing {emailLogs.length} of {totalRecords} Logged Emails</span>

            <span className="email-footer-separator">•</span>

            <span>{notificationsCount} Notifications</span>

            <span className="email-footer-separator">•</span>

            <span>{remindersCount} Reminder{remindersCount === 1 ? "" : "s"}</span>
          </div>
        </div>
      </div>

      {/* Email Details Drawer */}
      {selectedLog && (
        <>
        <style>
          {`
            body {
              overflow: hidden;
            }
          `}
        </style>
          <div
            className="email-details-backdrop"
            onClick={() => setSelectedLog(null)}
          ></div>

          <div className="email-details-panel">
            <div className="email-details-header">
              <h2>Email details</h2>
              <button
                className="email-details-close"
                type="button"
                title="Close"
                onClick={() => setSelectedLog(null)}
              >
                <i className="ri-close-line"></i>
              </button>
            </div>

            <div className="email-details-body">
              <div className="email-details-field">
                <p className="email-details-label">Client/Lead</p>
                <p className="email-details-value">{selectedLog?.Client?.client_name || "-"}</p>
              </div>

              <div className="email-details-field">
                <p className="email-details-label">Type</p>
                <span className={`email-type-badge ${String(selectedLog.type || "").toLowerCase()}`}>
                  {selectedLog.type}
                </span>
              </div>

              <div className="email-details-field">
                <p className="email-details-label">Trigger</p>
                <span className="email-trigger-badge">{selectedLog.trigger}</span>
              </div>

              <div className="email-details-field">
                <p className="email-details-label">Subject</p>
                <p className="email-details-value">{selectedLog.subject || "-"}</p>
              </div>

              <div className="email-details-field">
                <p className="email-details-label">Fee Gap at Send</p>
                <span className={`email-fee-gap ${selectedLog.fee_gap === "£0.00" ? "zero" : ""}`}>
                  {selectedLog.fee_gap || "£0.00"}
                </span>
              </div>

              <div className="email-details-field">
                <p className="email-details-label">Sent</p>
                <p className="email-details-value">{formatSentAt(selectedLog.sent_at)}</p>
              </div>

              <div className="email-details-field">
                <p className="email-details-label">Status</p>
                <span className={`email-status-badge ${String(selectedLog.status || "").toLowerCase().replace(/\s+/g, "-")}`}>
                  {selectedLog.status}
                </span>
              </div>
            </div>
          </div>

        </>
      )}
    </div>
  );
};

export default FeeProtectorEmailLog;
